// language-switcher.tsx

import React from "react";
import { ToggleButton, ToggleButtonGroup, Box, Typography, type SxProps, type Theme } from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { saveLanguage } from "@/i18n/languagePersistence";
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from "@/types/Languages.types";
import { useAppNavigate } from "@/hooks/useAppNavigate";

const LANGUAGE_OPTIONS: { code: SupportedLanguage; label: string }[] = [
  { code: "en-us", label: "English" },
  { code: "zh-hant", label: "繁體中文" },
  { code: "zh-hans", label: "簡體中文" },
];

export interface LanguageSwitcherProps {
  /** Renders as a fixed floating widget at bottom-right if true. Default is false (inline). */
  isFloating?: boolean;
  /** Custom wrapper SX styles */
  sx?: SxProps<Theme>;
  /** Size variant for buttons */
  size?: "small" | "medium";
}

export default function LanguageSwitcher({ isFloating = false, sx, size = "medium" }: LanguageSwitcherProps) {
  const navigate = useAppNavigate();
  const location = useLocation();
  const { pathname, search, state } = location;
  const { i18n } = useTranslation();

  // Normalize any locale code strictly into en-us, zh-hant, or zh-hans
  const getNormalizedLanguage = (currentLang: string): SupportedLanguage => {
    const lang = currentLang.toLowerCase();

    if (lang === "zh-hans" || lang === "zh-cn" || lang === "zh-sg" || lang.includes("hans")) {
      return "zh-hans";
    }
    if (lang === "zh-hant" || lang === "zh-tw" || lang === "zh-hk" || lang === "zh" || lang.includes("hant")) {
      return "zh-hant";
    }
    return "en-us";
  };

  const currentLanguage = getNormalizedLanguage(i18n.language || "en-us");

  const switchTo = (_: React.MouseEvent<HTMLElement>, newLang: SupportedLanguage | null) => {
    if (!newLang || newLang === currentLanguage) return;

    // 1. Save and change language
    saveLanguage(newLang);
    i18n.changeLanguage(newLang);

    // 2. Extract current route path segments
    const segments = pathname.split("/").filter(Boolean);

    const isFirstSegmentLang =
      segments.length > 0 &&
      ((SUPPORTED_LANGUAGES as readonly string[]).includes(segments[0]) ||
        ["en", "zh", "zh-hk", "zh-tw", "zh-cn", "en-us", "zh-hant", "zh-hans"].includes(segments[0].toLowerCase()));

    let newPath: string;
    if (isFirstSegmentLang) {
      segments[0] = newLang;
      newPath = "/" + segments.join("/");
    } else {
      newPath = `/${newLang}${pathname === "/" ? "" : pathname}`;
    }

    newPath = newPath.replace(/\/+/g, "/");

    // 3. Navigate while keeping location query parameters and state intact
    navigate(newPath + search, { replace: true, state });
  };

  const floatingStyles: SxProps<Theme> = isFloating
    ? {
        position: "fixed",
        bottom: { xs: 24, sm: 32 },
        right: { xs: 16, sm: 32 },
        zIndex: 1300,
      }
    : {};

  return (
    <Box sx={{ display: "inline-block", ...floatingStyles, ...sx }}>
      <ToggleButtonGroup
        value={currentLanguage}
        exclusive
        onChange={switchTo}
        aria-label="language switch"
        sx={{
          bgcolor: "background.paper",
          backdropFilter: "blur(16px)",
          borderRadius: 2,
          boxShadow: (theme) =>
            theme.palette.mode === "light" ? "0 4px 20px rgba(0, 0, 0, 0.08)" : "0 4px 20px rgba(0, 0, 0, 0.4)",
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
          "& .MuiToggleButtonGroup-grouped": {
            border: "none",
            "&:not(:last-of-type)": {
              borderRight: "1px solid",
              borderColor: "divider",
            },
          },
        }}
      >
        {LANGUAGE_OPTIONS.map(({ code, label }) => {
          const isSelected = currentLanguage === code;
          return (
            <ToggleButton
              key={code}
              value={code}
              sx={{
                px: size === "small" ? 2 : 2.5,
                py: size === "small" ? 0.8 : 1.2,
                color: isSelected ? "text.primary" : "text.secondary",
                bgcolor: isSelected ? "action.selected" : "transparent",
                fontWeight: isSelected ? 700 : 500,
                textTransform: "none",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  bgcolor: "action.hover",
                  color: "text.primary",
                },
              }}
            >
              <LanguageIcon
                sx={{
                  fontSize: size === "small" ? 14 : 16,
                  mr: 0.8,
                  color: isSelected ? "primary.main" : "inherit",
                }}
              />
              <Typography
                variant="body2"
                component="span"
                sx={{
                  fontSize: size === "small" ? "0.75rem" : "0.85rem",
                  fontWeight: isSelected ? 700 : 500,
                  color: "inherit",
                }}
              >
                {label}
              </Typography>
            </ToggleButton>
          );
        })}
      </ToggleButtonGroup>
    </Box>
  );
}
